import styled, { css } from 'styled-components';

import { pickColor } from 'themes/helpers/pickColor';

type OrderHeaderType = {
  leftMargin?: string;
  marginBottom?: string;
};

type MetaBlockProps = OrderHeaderType & {
  /** Label alignment over the value box — start for the left group, end for the totals. */
  $align?: 'start' | 'end';
};

type MetaValueProps = {
  $bold?: boolean;
  $wide?: boolean;
};

const Container = styled.div`
  padding: 2.5rem 0 1.2rem 0;
  margin-bottom: 2.5rem;

  border-bottom: 0.1rem solid ${pickColor('gray')};
`;

const HeaderTop = styled.div<OrderHeaderType>`
  display: flex;
  /* Narrow screens: the action buttons move under the title (and wrap there). */
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  margin-bottom: ${({ marginBottom }) => marginBottom};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  min-width: 0;
  margin-left: auto;
`;

const DateInfo = styled.span`
  display: block;
  margin-top: 0.8rem;
  font-size: 1.4rem;
  font-family: 'LFT Zeno', sans-serif;
  color: ${pickColor('grayDark')};
`;

/** Order number / dates / totals — wraps on narrow screens. */
const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1.2rem 1.6rem;
`;

const MetaBlock = styled.div<MetaBlockProps>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align = 'start' }) => ($align === 'end' ? 'flex-end' : 'flex-start')};
  gap: 0.4rem;
  font-size: 1.4rem;
  font-family: 'LFT Zeno', sans-serif;
  color: ${pickColor('grayDark')};
  margin-left: ${({ leftMargin }) => leftMargin};
`;

const MetaKey = styled.span`
  font-weight: 600;
  color: ${pickColor('blackLight')};
  text-transform: capitalize;

  &::after {
    content: ':';
  }
`;

/** Value in a bordered box, right-aligned and vertically centred like the mockup. */
const MetaValue = styled.span<MetaValueProps>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: ${({ $wide }) => ($wide ? '16rem' : '11rem')};
  height: 3.2rem;
  /* LFT Zeno digits sit ~1px above the line-box centre — nudge them down. */
  padding: 0.2rem 1rem 0;
  font-size: 1.6rem;
  line-height: 1;

  border: 0.1rem solid ${pickColor('borderColorDark')};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  background-color: ${pickColor('whiteDark')};

  color: ${pickColor('black')};
  text-align: right;
  white-space: nowrap;

  ${({ $bold }) =>
    $bold &&
    css`
      font-weight: 700;
    `};
`;

const MockNotice = styled.p`
  margin: 0.8rem 0 0.4rem 0;
  font-size: 1.2rem;
  font-style: italic;
  color: ${pickColor('grayDark')};
`;

export {
  Container,
  DateInfo,
  HeaderRight,
  HeaderTop,
  MetaBlock,
  MetaKey,
  MetaRow,
  MetaValue,
  MockNotice,
};
